/**
 * Traceability record for one AI detection — the evidence behind an AI-generated
 * marker. Duallo admin only.
 *
 * The detection that became the marker is the canonical record (isDuplicate
 * false); other detections judged to be the same anomaly carry isDuplicate true
 * and point at the canonical record via duplicateOfId.
 */
export default class MarkerProvenance {
    id = null;
    scanId = null;

    projectId = null;
    roboflowProjectId = null;
    workflowId = null;
    roboflowWorkflowId = null;
    mode = null;              // "Training" | "Live"

    reviewStatus = null;      // "Unreviewed" | "TruePositive" | "FalsePositive"
    correctedLabel = null;
    reviewedBy = null;
    reviewedByName = null;
    reviewedAt = null;
    reviewNotes = null;

    // Reviewer-corrected bbox (image px, centre x/y + w/h); null = use the model's box.
    correctedBboxX = null;
    correctedBboxY = null;
    correctedBboxWidth = null;
    correctedBboxHeight = null;

    isDuplicate = false;
    duplicateOfId = null;
    markerIndex = null;
    markerId = null;        // durable link to the created marker (preferred over nearest-position)

    label = null;
    confidence = 0;

    position = null;
    size = null;

    sourceImageFileName = null;
    detectionId = null;

    // Roboflow bbox in image pixels: centre x/y plus width/height.
    bboxX = 0;
    bboxY = 0;
    bboxWidth = 0;
    bboxHeight = 0;

    pointIndex = 0;
    candidateCount = 0;
    inlierCount = 0;
    depth = 0;

    createdAt = null;
    eTag = null;

    constructor(record) {
        this.id = record.id;
        this.scanId = record.scanId;

        this.projectId = record.projectId;
        this.roboflowProjectId = record.roboflowProjectId;
        this.workflowId = record.workflowId;
        this.roboflowWorkflowId = record.roboflowWorkflowId;
        this.mode = record.mode;

        this.reviewStatus = record.reviewStatus;
        this.correctedLabel = record.correctedLabel;
        this.reviewedBy = record.reviewedBy;
        this.reviewedByName = record.reviewedByName;
        this.reviewedAt = record.reviewedAt;
        this.reviewNotes = record.reviewNotes;

        this.correctedBboxX = record.correctedBboxX ?? null;
        this.correctedBboxY = record.correctedBboxY ?? null;
        this.correctedBboxWidth = record.correctedBboxWidth ?? null;
        this.correctedBboxHeight = record.correctedBboxHeight ?? null;

        this.isDuplicate = record.isDuplicate ?? false;
        this.duplicateOfId = record.duplicateOfId;
        this.markerIndex = record.markerIndex;
        this.markerId = record.markerId ?? null;

        this.label = record.label;
        this.confidence = record.confidence ?? 0;

        this.position = record.position;
        this.size = record.size;

        this.sourceImageFileName = record.sourceImageFileName;
        this.detectionId = record.detectionId;

        this.bboxX = record.bboxX ?? 0;
        this.bboxY = record.bboxY ?? 0;
        this.bboxWidth = record.bboxWidth ?? 0;
        this.bboxHeight = record.bboxHeight ?? 0;

        this.pointIndex = record.pointIndex ?? 0;
        this.candidateCount = record.candidateCount ?? 0;
        this.inlierCount = record.inlierCount ?? 0;
        this.depth = record.depth ?? 0;

        this.createdAt = record.createdAt;
        this.eTag = record.eTag;
    }

    /** Bounding box as top-left origin plus extents, which is what an overlay needs. */
    get bboxRect() {
        return {
            left: this.bboxX - this.bboxWidth / 2,
            top: this.bboxY - this.bboxHeight / 2,
            width: this.bboxWidth,
            height: this.bboxHeight
        };
    }
}
